import Vue from 'vue'
import Vuex from 'vuex'
import router from '@/router'
import * as firebase from '@/plugins/Firebase'

// Vue.config.devtools = process.env.NODE_ENV === 'development'

/**
 * @module vue/store
 * @desc See Vue Router {@link https://vuex.vuejs.org/guide/}
 * @requires Vue
 * @requires vuex
 * @requires Firebase
*/

Vue.use(Vuex)
const V = Vue.prototype;

/**@typedef state
 * @desc Vuex State Object
 * @property {object} platform Platform-wide Config
 * @property {string} platform.name Platform Display Name
 * @property {string} mode Variable Idicating User Role
 * @property {object} user Firebase Auth Object
 * @property {object} listener Firebase Firestore Listener Objects
*/

/**@typedef mutations
 * @desc Vuex Mutations
 * @property {function} add insert item into state
 * @property {function} set create/upate item in state
 * @property {function} del delete item in state
 * @property {function} setListener insert active listener in state
 * @property {function} delListener kill and remove listener in state
*/

/**@typedef actions
 * @desc Vuex Dispatch Actions
 * @property {function} connectDB Listen to changes in a Firebase Firestore Collection
 * @property {function} fetchDB Fetch a Firebase Firestore Collection Once
 * @property {function} watchDoc Listen to changes in a Firebase Firestore Document
 * @property {function} fetchDoc Fetch a Firebase Firestore Document Once
 * @property {function} disconnectDB Delete a listener created by connectDB
 * @property {function} refreshUser Fetch and merge Current User's details from Firebase Auth and Firebase Firestore
 * @property {function} signOut Log Out the current user
 * @property {function} signIn Trigger the Login process
*/
const VueStore = {
    state: {
        platform: {
            name: "OSCA"
        },
        mode: "merchant",
        user: null,
        listeners: {},
    },
    mutations: {
        add(state, item) {
            if (state[item.store] === undefined) {
                Vue.set(state, item.store, {});
            }
            Vue.set(state[item.store], item.id, item.data);
        },
        set(state, item) {
            Vue.set(state, item.store, item.data);
        },
        del(state, item) {
            delete state[item.store][item.id];
        },
        clear(state, item) {
            Vue.set(state, item, {});
        },
        setListener(state, listener) {
            state.listeners[listener.target] = listener.fx;
        },
        delListener(state, listener) {
            state.listeners[listener.target]();
            state.listeners[listener.target] = null;
        }
    },
    actions: {
        connectDB({ commit, state }, payload) {
            let db = Vue.prototype.$firebase.firestore().collection(payload.path);
            let store = payload.store;
            if (state.user.claims.role !== "admin" && state.user.claims.role !== "observer") {
                db = db.where("uid", "==", state.user.uid);
            }
            if (typeof payload.where === "object") {
                Object.keys(payload.where).forEach(field => {
                    if (typeof payload.where[field] === "object") {
                        let operator = payload.where[field].operator ? payload.where[field].operator : '==';
                        db = db.where(field, operator, payload.where[field].value);
                    } else {
                        db = db.where(field, "==", payload.where[field]);
                    }
                })
            }
            if (typeof payload.order === "object") {
                db = db.orderBy(payload.order.field, payload.order.order)
            } else if (typeof payload.order === "string") {
                db = db.orderBy(payload.order, "asc")
            }
            if (typeof payload.limit === "number") {
                db = db.limit(payload.limit);
            }
            if (!state.listeners[store]) {
                let listener = { target: store };
                listener.fx = db.onSnapshot(docs => {
                    if (!docs.empty) {
                        docs.docChanges().forEach(change => {
                            let data = change.doc.data();
                            data.id = change.doc.id;
                            let item = {
                                id: change.doc.id,
                                data: data,
                                store: store
                            }
                            switch (change.type) {
                                case "removed":
                                    commit('del', item);
                                    break;
                                default:
                                    commit('add', item);
                            }
                        })
                    } else {
                        let item = {
                            store,
                            data: {}
                        }
                        commit('set', item)
                    }
                });
                commit('setListener', listener);
            }
        },
        fetchDB({ commit, state }, payload) {
            let db = Vue.prototype.$firebase.firestore().collection(payload.path);
            let store = payload.store;
            if (state.user.claims.role !== "admin" && state.user.claims.role !== "gov") {
                db = db.where("uid", "==", state.user.uid);
            }
            if (typeof payload.where === "object") {
                Object.keys(payload.where).forEach(field => {
                    if (typeof payload.where[field] === "object") {
                        let operator = payload.where[field].operator ? payload.where[field].operator : '==';
                        db = db.where(field, operator, payload.where[field].value);
                    } else {
                        db = db.where(field, "==", payload.where[field]);
                    }
                })
            }
            if (typeof payload.order === "object") {
                db = db.orderBy(payload.order.field, payload.order.order)
            } else if (typeof payload.order === "string") {
                db = db.orderBy(payload.order, "asc")
            }
            if (typeof payload.limit === "number") {
                db = db.limit(payload.limit);
            }
            db.get().then(docs => {
                if (docs.size > 0) {
                    docs.forEach(doc => {
                        let data = doc.data();
                        data.id = doc.id;
                        let item = {
                            id: doc.id,
                            data: data,
                            store: store
                        }
                        commit('add', item);
                    })
                } else {
                    let item = {
                        store,
                        data: {}
                    }
                    commit('set', item)
                }
            })
        },
        watchDoc({ commit, state }, payload) {
            let db = Vue.prototype.$firebase.firestore().doc(payload.path);
            let store = payload.store;
            if (!state.listeners[store]) {
                let listener = { target: store };
                listener.fx = db.onSnapshot(doc => {
                    let data = doc.data();
                    data.id = doc.id;
                    let item = {
                        data: data,
                        store: store
                    }
                    commit('set', item);
                });
                commit('setListener', listener);
            }
        },
        fetchDoc({ commit, state }, payload) {
            let db = Vue.prototype.$firebase.firestore().doc(payload.path);
            if (!payload.refresh && state[payload.store] && state[payload.store][payload.id]) {
                return Promise.resolve(state[payload.store][payload.id]);
            }
            return db.get().then(doc => {
                if (doc.exists) {
                    let item = {
                        id: payload.id,
                        store: payload.store,
                        data: doc.data(),
                    }
                    commit('add', item);
                    return item.data;
                }
            })
        },
        disconnectDB({ commit, state }) {
            Object.keys(state.listeners).forEach(key => {
                commit('delListener', { target: key })
            })
        },
        async refreshUser({ commit }) {
            let user = V.$firebase.auth().currentUser;
            if (user) {
                let [token, doc] = await Promise.all([
                    user.getIdTokenResult(true).catch(() => { return null }),
                    V.$firebase.firestore().doc(`auth/${user.uid}`).get(),
                ]);
                user.claims = token.claims;
                if (doc.exists) {
                    let data = doc.data();
                    Object.assign(user, data);
                }
                commit('set', { store: 'user', data: user })
                return user;
            } else {
                commit('set', { store: 'user', data: null })
                return Promise.resolve(null);
            }
        },
        signOut({ commit }) {
            V.$firebase.auth().signOut().then(() => {
                V.$M.toast({ html: "Sign Out Successful" });
                commit('set', { store: 'user', data: null })
                router.replace("/");
            })
        },
        signIn({ dispatch }) {
            var provider = new V.$firebase.auth.GoogleAuthProvider();
            return V.$firebase.auth().signInWithPopup(provider)
                .then(() => {
                    dispatch('refreshUser');
                })
                .catch(function (error) {
                    V.$M.toast({ html: error.message });
                });
        }
    },
    modules: {
        firebase: firebase.store
    }
}
export default new Vuex.Store(VueStore)
